import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { salesApi } from '../lib/api';
import AllSitesLink from '../components/AllSitesLink';
import CustomerSynopsis from '../components/CustomerSynopsis';
import StaffSignIn from '../components/StaffSignIn';
import useStaffSession from '../hooks/useStaffSession';

// Sales tool — sales.silverservice.com. Staff-facing: a sales_rep sees their
// HubSpot-owned book; owners see every account. First view is the unified
// customer master (one row per account across SOS/QBO/HubSpot/Sage). Revenue
// scorecard + Customer 360 build on this next.
export default function Sales() {
  const session = useStaffSession('sales');
  const { token } = session;
  const [me, setMe] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [refreshingMetrics, setRefreshingMetrics] = useState(false);
  const [selectedKey, setSelectedKey] = useState(null);
  const [view, setView] = useState('accounts');
  const [ownerFilter, setOwnerFilter] = useState('all');
  const [sortKey, setSortKey] = useState('');

  const loadData = useCallback(() => {
    setLoading(true);
    setError('');
    Promise.all([salesApi.me(), salesApi.getAccounts()])
      .then(([m, a]) => { setMe(m); setData(a); })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (token) loadData();
  }, [token, loadData]);

  // The server starts a refresh on its own when the numbers are old; wait for
  // it and reload so the page fills in without anyone pressing a button.
  const autoRunning = data?.metricsStatus === 'running';
  useEffect(() => {
    if (!autoRunning || refreshingMetrics) return undefined;
    let cancelled = false;
    (async () => {
      for (let i = 0; i < 24 && !cancelled; i++) {
        await new Promise((r) => setTimeout(r, 5000));
        const s = await salesApi.getMetricsStatus().catch(() => null);
        if (s && s.status !== 'running') break;
      }
      if (!cancelled) loadData();
    })();
    return () => { cancelled = true; };
  }, [autoRunning, refreshingMetrics, loadData]);

  // Recompute now: kicks the worker, polls until it finishes, then reloads
  // the accounts so they pick up the fresh numbers.
  async function refreshMetrics() {
    setRefreshingMetrics(true);
    setError('');
    try {
      await salesApi.refreshMetrics();
      for (let i = 0; i < 60; i++) {
        await new Promise((r) => setTimeout(r, 5000));
        const s = await salesApi.getMetricsStatus();
        if (s.status !== 'running') break;
      }
      await loadData();
    } catch (e) { setError(e.message); }
    finally { setRefreshingMetrics(false); }
  }

  const accounts = data?.accounts || [];
  const ownerNames = useMemo(() => [...new Set(accounts.map((a) => a.ownerName).filter(Boolean))].sort(), [accounts]);
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = accounts.filter((a) => {
      if (statusFilter !== 'all' && (!a.metrics || a.metrics.status !== statusFilter)) return false;
      if (ownerFilter === 'none' && a.ownerName) return false;
      if (ownerFilter !== 'all' && ownerFilter !== 'none' && a.ownerName !== ownerFilter) return false;
      if (!q) return true;
      return a.name.toLowerCase().includes(q) || (a.ownerName || '').toLowerCase().includes(q);
    });
    return sortKey ? list.slice().sort((a, b) => SPEND_SORTS[sortKey](b) - SPEND_SORTS[sortKey](a)) : list;
  }, [accounts, query, statusFilter, ownerFilter, sortKey]);
  const totals = useMemo(() => bookTotals(filtered), [filtered]);
  const periods = data?.spendPeriods;

  const metricsStatus = data?.metricsStatus || 'idle';
  const metricsBusy = refreshingMetrics || metricsStatus === 'running';
  const hasMetrics = accounts.some((a) => a.metrics);

  if (!token) return <StaffSignIn session={session} siteName="Sales" />;

  return (
    <div style={st.page}>
      <div style={st.topBar}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <img src="/logo.png" alt="Silver Service" style={st.logoImg} />
          <span style={st.brandSmSub}>Sales</span>
        </div>
        <div style={st.topRight}>
          <span style={st.who}>{me?.name || me?.email}{me?.isOwner ? ' · Owner' : ''}</span>
          <AllSitesLink style={st.linkBtn} />
          <button style={st.linkBtn} onClick={session.signOut} title="Signs this browser out of every staff site">Sign out</button>
        </div>
      </div>

      <div style={st.body}>
        {!selectedKey && (
          <div style={st.viewTabs}>
            <button style={view === 'accounts' ? { ...st.viewTab, ...st.viewTabActive } : st.viewTab} onClick={() => setView('accounts')}>Accounts</button>
            <button style={view === 'winback' ? { ...st.viewTab, ...st.viewTabActive } : st.viewTab} onClick={() => setView('winback')}>Win-back</button>
            <button style={view === 'scorecard' ? { ...st.viewTab, ...st.viewTabActive } : st.viewTab} onClick={() => setView('scorecard')}>Scorecard</button>
            <button style={view === 'leads' ? { ...st.viewTab, ...st.viewTabActive } : st.viewTab} onClick={() => setView('leads')}>Leads</button>
            <button style={view === 'tasks' ? { ...st.viewTab, ...st.viewTabActive } : st.viewTab} onClick={() => setView('tasks')}>My tasks</button>
          </div>
        )}

        {selectedKey ? (
          <AccountDetail accountKey={selectedKey} me={me} onBack={() => setSelectedKey(null)} />
        ) : view === 'scorecard' ? (
          <Scorecard isOwner={!!data?.all} />
        ) : view === 'leads' ? (
          <Leads me={me} />
        ) : view === 'winback' ? (
          <Winback onOpenAccount={setSelectedKey} />
        ) : view === 'tasks' ? (
          <MyTasks onOpenAccount={setSelectedKey} />
        ) : (
        <>
        <div style={st.titleRow}>
          <h2 style={st.h2}>{data?.all ? 'All accounts' : 'My book'}</h2>
          <button style={st.primarySm} onClick={refreshMetrics} disabled={metricsBusy || loading}>
            {metricsBusy ? 'Updating…' : 'Refresh numbers'}
          </button>
        </div>

        {error && <p style={st.err}>{error}</p>}

        {data && !data.all && !data.bookAssigned && (
          <div style={st.notice}>
            No accounts are assigned to you yet. Olaf or Alet can assign them from each account’s
            page (Owner → Change); they’ll appear here straight away.
          </div>
        )}

        {metricsBusy && (
          <div style={st.notice}>Updating spend, GP and health from the latest orders… this takes a few seconds. You can keep browsing — it’ll fill in when done.</div>
        )}

        {data && (
          <div style={st.kpis}>
            <div style={st.kpi}><div style={st.kpiNum}>{fmtMoney(totals.avgMonthly)}</div><div style={st.kpiLbl}>Avg / month</div></div>
            <div style={st.kpi}><div style={st.kpiNum}>{fmtMoney(totals.thisQuarter)}</div><div style={st.kpiLbl}>{periods ? `${periods.qtd.label} so far` : 'This quarter so far'}</div><YoY now={totals.thisQuarter} then={totals.thisQuarterLy} /></div>
            <div style={st.kpi}><div style={st.kpiNum}>{fmtMoney(totals.lastQuarter)}</div><div style={st.kpiLbl}>{periods ? periods.lastQ.label : 'Last quarter'}</div><YoY now={totals.lastQuarter} then={totals.lastQuarterLy} /></div>
            <div style={st.kpi}><div style={st.kpiNum}>{fmtMoney(totals.gp)}</div><div style={st.kpiLbl}>GP, last 12 months{totals.gpPct != null ? ` · ${totals.gpPct.toFixed(1)}%` : ''}</div></div>
            <div style={st.kpi}><div style={st.kpiNum}>{filtered.length}</div><div style={st.kpiLbl}>Accounts{filtered.length !== data.counts.total ? ` (of ${data.counts.total})` : ''}</div></div>
          </div>
        )}

        {hasMetrics && data.statusCounts && (
          <div style={st.chips}>
            {[['all', 'All'], ['healthy', 'Healthy'], ['at-risk', 'At-risk'], ['dormant', 'Dormant']].map(([key, label]) => (
              <button
                key={key}
                onClick={() => setStatusFilter(key)}
                style={{ ...st.chip, ...(statusFilter === key ? st.chipActive : {}), ...(key !== 'all' ? { color: STATUS_META[key].color } : {}) }}
              >
                {label}{key !== 'all' ? ` (${data.statusCounts[key] || 0})` : ''}
              </button>
            ))}
          </div>
        )}

        {hasMetrics && (
          <div style={st.asOf}>
            Numbers as of {data.metricsFetchedAt ? new Date(data.metricsFetchedAt).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—'}
            <InfoTip align="left" width={320}>{METRICS_HELP}</InfoTip>
          </div>
        )}

        <div style={st.filterRow}>
          <input style={{ ...st.search, margin: 0, flex: '1 1 240px' }} placeholder="Search accounts or owner…" value={query} onChange={(e) => setQuery(e.target.value)} />
          {data?.all && (
            <select style={st.select} value={ownerFilter} onChange={(e) => setOwnerFilter(e.target.value)} aria-label="Filter by owner">
              <option value="all">Every owner</option>
              {ownerNames.map((n) => <option key={n} value={n}>{n}</option>)}
              <option value="none">No owner</option>
            </select>
          )}
        </div>

        {loading && !data && <p style={st.muted}>Loading accounts…</p>}

        {data && (
          <div style={st.tableScroll}>
          <table style={st.table}>
            <thead>
              <tr>
                <th style={st.th}>Account</th>
                {data.all && <th style={st.th}>Owner</th>}
                <SortTh k="avgMonthly" sortKey={sortKey} setSortKey={setSortKey} tip="The last 12 full months of spend ÷ 12 — or ÷ the months since their first order, if they’re newer.">Avg&nbsp;/&nbsp;mo</SortTh>
                <SortTh k="thisQuarter" sortKey={sortKey} setSortKey={setSortKey} tip="Spend this calendar quarter up to today. The arrow compares the same days last year.">{periods ? `${periods.qtd.label}` : 'This qtr'} so&nbsp;far</SortTh>
                <SortTh k="lastQuarter" sortKey={sortKey} setSortKey={setSortKey} tip="Spend last calendar quarter. The arrow compares the same quarter a year earlier, once there’s SOS history for it (SOS starts 20 Aug 2025).">{periods ? periods.lastQ.label : 'Last qtr'}</SortTh>
                <SortTh k="gp" sortKey={sortKey} setSortKey={setSortKey} tip="Gross profit over the last 12 full months: spend minus cost, with GP %. * means cost is known for less than 90% of the spend.">GP&nbsp;(12&nbsp;mo)</SortTh>
                <th style={st.th}>Last order<InfoTip align="left" width={260}>Date of their most recent SOS order. ⚠ means overdue — past 1.5× their usual gap between orders.</InfoTip></th>
                <th style={st.th}>Status<InfoTip align="right" width={300}>{HEALTH_HELP}</InfoTip></th>
                <th style={st.th}>Sources</th>
              </tr>
            </thead>
            <tbody>
              {filtered.slice(0, 500).map((a) => (
                <tr key={a.key} style={st.rowClickable} onClick={() => setSelectedKey(a.key)}>
                  <td style={st.tdLink}>{a.name}</td>
                  {data.all && <td style={st.td}>{a.ownerName || '\u2014'}</td>}
                  <SpendCells spend={a.spend} />
                  <td style={st.td}>{a.metrics && a.metrics.lastOrderDate ? `${a.metrics.lastOrderDate}${a.metrics.overdue ? ' \u26a0' : ''}` : <span style={st.muted}>{'\u2014'}</span>}</td>
                  <td style={st.td}>{a.metrics ? <span style={{ ...st.statusBadge, background: STATUS_META[a.metrics.status].bg, color: STATUS_META[a.metrics.status].color }}>{STATUS_META[a.metrics.status].label}</span> : <span style={st.muted}>{'\u2014'}</span>}</td>
                  <td style={st.td}>
                    {['sos', 'qbo', 'hubspot', 'sage'].filter((s) => a.systems[s]).map((s) => (
                      <span key={s} style={st.badge}>{SRC_LABEL[s]}</span>
                    ))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
        {filtered.length > 500 && <p style={st.muted}>Showing first 500 of {filtered.length}. Refine your search.</p>}
        {data && filtered.length === 0 && <p style={st.muted}>No accounts match.</p>}
        </>
        )}
      </div>
    </div>
  );
}

const SRC_LABEL = { sos: 'SOS', qbo: 'QBO', hubspot: 'HubSpot', sage: 'Sage' };
const STATUS_META = {
  healthy: { label: 'Healthy', color: '#1a7f37', bg: '#e6f4ea' },
  'at-risk': { label: 'At-risk', color: '#b26a00', bg: '#fff4e5' },
  dormant: { label: 'Dormant', color: '#b3261e', bg: '#fdecea' },
};
const fmtMoney = (n) => '$' + Math.round(n || 0).toLocaleString('en-US');
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const monthShort = (key) => MONTHS[Number(String(key).split('-')[1]) - 1] || key;

// Customer 360 drill-down: the full story for one account.
function AccountDetail({ accountKey, me, onBack }) {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;
    setLoading(true); setError('');
    salesApi.getAccount(accountKey)
      .then((res) => { if (alive) setD(res); })
      .catch((e) => { if (alive) setError(e.message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [accountKey]);

  const back = <button style={st.backBtn} onClick={onBack}>← All accounts</button>;
  if (loading) return <div>{back}<p style={st.muted}>Loading account…</p></div>;
  if (error) return <div>{back}<p style={st.err}>{error}</p></div>;
  if (!d) return null;

  const a = d.account;
  const m = a.metrics;
  const sp = a.spend;

  return (
    <div>
      {back}
      <div style={st.detailHead}>
        <div>
          <h2 style={st.h2}>{a.name}</h2>
          <div style={st.detailSub}>
            <OwnerControl account={a} canChange={!!me?.isOwner}
              onChanged={(o) => setD((prev) => ({ ...prev, account: { ...prev.account, ownerId: o.ownerId, ownerName: o.ownerName } }))} />
            {['sos', 'qbo', 'hubspot', 'sage'].filter((s) => a.systems[s]).map((s) => (<span key={s} style={st.badge}>{SRC_LABEL[s]}</span>))}
          </div>
        </div>
        {m && <span style={{ ...st.statusBadge, background: STATUS_META[m.status].bg, color: STATUS_META[m.status].color }}>{STATUS_META[m.status].label}</span>}
      </div>

      {m ? (
        <div style={st.kpis}>
          <div style={st.kpi}><div style={st.kpiNum}>{sp && sp.avgMonthly != null ? fmtMoney(sp.avgMonthly) : '—'}</div><div style={st.kpiLbl}>Avg / month<InfoTip align="left" width={240}>The last 12 full months of spend ÷ 12 — or ÷ the months since their first order, if they’re newer.</InfoTip></div></div>
          <div style={st.kpi}><div style={st.kpiNum}>{sp ? fmtMoney(sp.last12.spend) : '—'}</div><div style={st.kpiLbl}>Spend, last 12 months</div></div>
          <div style={st.kpi}><div style={st.kpiNum}>{sp ? fmtMoney(sp.last12.gp) : '—'}</div><div style={st.kpiLbl}>GP, last 12 months{sp && sp.last12.gpPct != null ? ` · ${sp.last12.gpPct}%` : ''}{sp && sp.last12.costCoverage != null && sp.last12.costCoverage < 0.9 ? ` (cost known for ${Math.round(sp.last12.costCoverage * 100)}%)` : ''}</div></div>
          <div style={st.kpi}><div style={st.kpiNum}>{fmtMoney(m.avgOrderValue)}</div><div style={st.kpiLbl}>Avg order</div></div>
          <div style={st.kpi}><div style={st.kpiNum}>{m.cadenceDays != null ? m.cadenceDays + 'd' : '—'}</div><div style={st.kpiLbl}>Order cadence<InfoTip align="left" width={240}>The typical gap between this account’s orders (the median). It’s what “overdue” is measured against.</InfoTip></div></div>
          <div style={st.kpi}><div style={st.kpiNum}>{m.orderCount}</div><div style={st.kpiLbl}>Orders (12 mo)</div></div>
          <div style={st.kpi}><div style={st.kpiNum}>{m.lastOrderDate || '—'}{m.overdue ? ' ⚠' : ''}</div><div style={st.kpiLbl}>Last order<InfoTip align="right" width={270}>Date of their most recent order. {m.overdue ? `⚠ The warning means they’re overdue — silent for more than 1.5× their usual gap between orders${m.cadenceDays != null ? ` (about every ${m.cadenceDays} days)` : ''}.` : 'They’re ordering on their usual rhythm.'}</InfoTip></div></div>
        </div>
      ) : (
        <div style={st.notice}>No SOS order history in the last year — this account hasn’t ordered recently (or isn’t linked to SOS yet).</div>
      )}

      {sp && sp.quarters && <QuarterTable quarters={sp.quarters} />}

      <AccountSynopsis account={a} accountKey={accountKey} me={me} />

      <AccountActivity accountKey={accountKey} me={me} />

      <AccountOrders key={accountKey} accountKey={accountKey} initial={d} />

      <h4 style={st.sectionH}>Accounts receivable (QuickBooks)</h4>
      {!d.ar || !d.ar.matched ? (
        <p style={st.muted}>{d.ar && d.ar.error ? d.ar.error : 'No matching QuickBooks customer.'}</p>
      ) : (
        <div>
          <p style={st.arLine}>Open balance: <strong>{fmtMoney(d.ar.openBalance)}</strong> across {d.ar.openCount} invoice{d.ar.openCount === 1 ? '' : 's'}.</p>
          {d.ar.invoices.length > 0 && (
            <table style={st.table}>
              <thead><tr><th style={st.th}>Invoice</th><th style={st.th}>Date</th><th style={st.thR}>Total</th><th style={st.thR}>Balance</th><th style={st.th}>Status</th></tr></thead>
              <tbody>{d.ar.invoices.map((i) => (<tr key={i.id}><td style={st.td}>#{i.number || i.id}</td><td style={st.td}>{i.date}</td><td style={st.tdR}>{fmtMoney(i.total)}</td><td style={st.tdR}>{fmtMoney(i.balance)}</td><td style={st.td}>{i.status}</td></tr>))}</tbody>
            </table>
          )}
        </div>
      )}

      <h4 style={st.sectionH}>
        Pipeline (HubSpot)
        {d.lastActivity && <span style={st.lastAct}> · last contact {new Date(d.lastActivity).toLocaleDateString('en-US')}</span>}
      </h4>
      {!d.pipeline || d.pipeline.error ? (
        <p style={st.muted}>{d.pipeline && d.pipeline.error ? d.pipeline.error : 'No HubSpot company linked.'}</p>
      ) : d.pipeline.openCount === 0 ? (
        <p style={st.muted}>No open deals.</p>
      ) : (
        <div>
          <p style={st.arLine}>Open pipeline: <strong>{fmtMoney(d.pipeline.openValue)}</strong> across {d.pipeline.openCount} deal{d.pipeline.openCount === 1 ? '' : 's'}{d.pipeline.qualifiedCount ? ` (${d.pipeline.qualifiedCount} qualified)` : ''}.</p>
          <table style={st.table}>
            <thead><tr><th style={st.th}>Deal</th><th style={st.th}>Stage</th><th style={st.th}>Close date</th><th style={st.thR}>Value</th></tr></thead>
            <tbody>{d.pipeline.open.slice(0, 12).map((dl, i) => (
              <tr key={i}><td style={st.td}>{dl.name}</td><td style={st.td}>{dl.stage}</td><td style={st.td}>{dl.closeDate || '—'}</td><td style={st.tdR}>{fmtMoney(dl.amount)}</td></tr>
            ))}</tbody>
          </table>
        </div>
      )}

      <p style={st.comingSoon}>Coming soon: recommended products, and full Sage 50 history.</p>
    </div>
  );
}

const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const fmtFullDay = (day) => new Date(day + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
const isDay = (s) => /^\d{4}-\d{2}-\d{2}$/.test(s || '');

// In the viewer's own time zone, so "this month" is Colorado's month.
function presetRange(id, now = new Date()) {
  const y = now.getFullYear();
  const m = now.getMonth();
  const today = ymd(now);
  const q = Math.floor(m / 3);
  switch (id) {
    case 'thisMonth': return { from: ymd(new Date(y, m, 1)), to: today };
    case 'lastMonth': return { from: ymd(new Date(y, m - 1, 1)), to: ymd(new Date(y, m, 0)) };
    case 'lastQuarter': return { from: ymd(new Date(y, (q - 1) * 3, 1)), to: ymd(new Date(y, q * 3, 0)) };
    case 'ytd': return { from: `${y}-01-01`, to: today };
    case 'lastYear': return { from: `${y - 1}-01-01`, to: `${y - 1}-12-31` };
    case 'last12': return { from: ymd(new Date(y, m - 11, 1)), to: today };
    default: return null;
  }
}
const RANGE_PRESETS = [
  ['thisMonth', 'This month'], ['lastMonth', 'Last month'], ['lastQuarter', 'Last quarter'],
  ['ytd', 'Year to date'], ['lastYear', 'Last year'], ['last12', 'Last 12 months'], ['all', 'All history'],
];
// The first order in the SOS mirror; anything earlier lives in Sage 50.
const SOS_ORDERS_START = '2025-08-20';

function periodLabel(period, granularity, multiYear) {
  const [y, m, d] = period.split('-');
  if (granularity === 'week') return `${MONTHS[Number(m) - 1]} ${Number(d)}`;
  return multiYear ? `${MONTHS[Number(m) - 1]} '${y.slice(2)}` : MONTHS[Number(m) - 1];
}

// Revenue trend, orders and top products over a date range the viewer picks.
// The page arrives with the last 12 months; a new range re-asks only for the
// order history, not QuickBooks or HubSpot.
function AccountOrders({ accountKey, initial }) {
  const [h, setH] = useState(initial);
  const [preset, setPreset] = useState('last12');
  const [draft, setDraft] = useState(initial.range);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const seq = useRef(0);
  const requested = useRef(initial.range);

  const load = useCallback((params, presetId) => {
    const n = ++seq.current;
    setPreset(presetId); setLoading(true); setError('');
    salesApi.getAccountOrders(accountKey, params)
      .then((res) => {
        if (n !== seq.current) return;
        requested.current = res.range;
        setH(res); setDraft(res.range);
      })
      .catch((e) => { if (n === seq.current) setError(e.message); })
      .finally(() => { if (n === seq.current) setLoading(false); });
  }, [accountKey]);

  function pick(id) {
    const r = presetRange(id);
    if (r) { requested.current = r; setDraft(r); load(r, id); } else load({ range: 'all' }, id);
  }

  // Typed dates settle before asking, so a half-typed year doesn't fire a request.
  useEffect(() => {
    const r = requested.current;
    if (!isDay(draft.from) || !isDay(draft.to) || draft.from > draft.to) return undefined;
    if (r && r.from === draft.from && r.to === draft.to) return undefined;
    const t = setTimeout(() => { requested.current = draft; load(draft, 'custom'); }, 600);
    return () => clearTimeout(t);
  }, [draft, load]);

  const pts = h.revenueByPeriod;
  const maxRev = Math.max(1, ...pts.map((x) => x.revenue));
  const multiYear = h.range.from.slice(0, 4) !== h.range.to.slice(0, 4);
  // Fewer labels on a phone; every bar still gets its tooltip.
  const labelEvery = Math.ceil(pts.length / (window.innerWidth < 600 ? 6 : 18));
  const unit = h.granularity === 'week' ? 'week' : 'month';

  return (
    <div>
      <h4 style={st.sectionH}>Orders &amp; products</h4>
      <div style={st.rangeBar}>
        <div style={{ ...st.chips, margin: 0 }}>
          {RANGE_PRESETS.map(([id, label]) => (
            <button key={id} style={{ ...st.chip, ...(preset === id ? st.chipActive : null) }} onClick={() => pick(id)}>{label}</button>
          ))}
        </div>
        <div style={st.rangeDates}>
          <input style={st.dateInput} type="date" value={draft.from} max={draft.to} aria-label="From"
            onChange={(e) => setDraft((r) => ({ ...r, from: e.target.value }))} />
          <span style={st.muted}>to</span>
          <input style={st.dateInput} type="date" value={draft.to} min={draft.from} aria-label="To"
            onChange={(e) => setDraft((r) => ({ ...r, to: e.target.value }))} />
        </div>
      </div>
      <p style={st.rangeSummary}>
        {fmtFullDay(h.range.from)} – {fmtFullDay(h.range.to)} · <strong>{fmtMoney(h.totalRevenue)}</strong> across {h.totalOrders} order{h.totalOrders === 1 ? '' : 's'}
        {loading && <span style={st.muted}> · updating…</span>}
        {error && <span style={st.err}> · {error}</span>}
      </p>
      {h.range.from < SOS_ORDERS_START && (
        <p style={st.muted}>Orders before Aug 20, 2025 were kept in Sage 50 and aren’t included here yet.</p>
      )}

      <div style={{ opacity: loading ? 0.55 : 1, transition: 'opacity .15s' }}>
        <h4 style={st.subH}>Revenue by {unit}</h4>
        <div style={{ ...st.bars, gap: pts.length > 30 ? 2 : 6 }}>
          {pts.map((x, i) => (
            <div key={x.period} style={st.barCol}
              title={`${unit === 'week' ? 'Week of ' + fmtFullDay(x.period) : periodLabel(x.period, 'month', true)} — ${fmtMoney(x.revenue)} (${x.orders} order${x.orders === 1 ? '' : 's'})`}>
              <div style={{ ...st.bar, height: Math.round((x.revenue / maxRev) * 120) + 2 }} />
              <div style={st.barLabel}>{i % labelEvery === 0 ? periodLabel(x.period, h.granularity, multiYear) : ' '}</div>
            </div>
          ))}
        </div>

        <div style={st.twoCol}>
          <div style={st.col}>
            <h4 style={st.subH}>Orders ({h.totalOrders})</h4>
            {h.recentOrders.length === 0 ? <p style={st.muted}>No orders in this range.</p> : (
              <div style={st.scrollBox}>
                <table style={st.table}>
                  <thead><tr><th style={st.th}>Date</th><th style={st.th}>Order</th><th style={st.thR}>Total</th></tr></thead>
                  <tbody>{h.recentOrders.map((o) => (<tr key={o.number}><td style={st.td}>{o.date}</td><td style={st.td}>#{o.number}</td><td style={st.tdR}>{fmtMoney(o.total)}</td></tr>))}</tbody>
                </table>
              </div>
            )}
            {h.recentOrders.length < h.totalOrders && <p style={st.muted}>Showing the newest {h.recentOrders.length}.</p>}
          </div>
          <div style={st.col}>
            <h4 style={st.subH}>Top products</h4>
            {h.topProducts.length === 0 ? <p style={st.muted}>None in this range.</p> : (
              <table style={st.table}>
                <thead><tr><th style={st.th}>Product</th><th style={st.thR}>Qty</th><th style={st.thR}>Revenue</th></tr></thead>
                <tbody>{h.topProducts.map((p, i) => (<tr key={p.sku || i}><td style={st.td}>{p.name}</td><td style={st.tdR}>{p.qty}</td><td style={st.tdR}>{fmtMoney(p.revenue)}</td></tr>))}</tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

const fmtPct = (x) => (x > 0 ? '+' : '') + (x * 100).toFixed(1) + '%';
const MOVER_LABEL = { new: 'New', grown: 'Grown', declined: 'Declined', lost: 'Lost', flat: 'Flat' };
const moverStyle = (cat) => ({
  new: { background: '#e6f4ea', color: '#1a7f37' },
  grown: { background: '#e6f4ea', color: '#1a7f37' },
  declined: { background: '#fff4e5', color: '#b26a00' },
  lost: { background: '#fdecea', color: '#b3261e' },
  flat: { background: '#f3f3f3', color: '#777' },
}[cat] || { background: '#f3f3f3', color: '#777' });

// Growth-vs-baseline scorecard — Matthew's bonus KPI.
function Scorecard({ isOwner }) {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [label, setLabel] = useState('');
  const [target, setTarget] = useState('5');
  const [customTarget, setCustomTarget] = useState('');

  const load = useCallback(() => {
    setLoading(true); setError('');
    salesApi.getScorecard().then(setD).catch((e) => setError(e.message)).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  async function snapshot() {
    setBusy(true); setError('');
    try { await salesApi.setBaseline({ label: label.trim() || undefined, targetPct: (Number(target) || 0) / 100 }); await load(); }
    catch (e) { setError(e.message); } finally { setBusy(false); }
  }
  async function changeTarget(pct) {
    setBusy(true); setError('');
    try { await salesApi.setTarget(pct / 100); await load(); } catch (e) { setError(e.message); } finally { setBusy(false); }
  }

  if (loading) return <p style={st.muted}>Loading scorecard…</p>;
  if (error) return <p style={st.err}>{error}</p>;
  if (!d) return null;

  if (!d.hasBaseline) {
    return (
      <div>
        <h2 style={st.h2}>Growth scorecard</h2>
        {isOwner ? (
          d.metricsAvailable ? (
            <div style={st.kpi}>
              <p style={st.sub}>Set a baseline to start measuring growth. It freezes each account’s current 3-month revenue as the starting line; growth is then measured against it (Q1 +5%, Q2 +10%).</p>
              <div style={st.baselineForm}>
                <input style={st.search} placeholder="Label (e.g. 2026 baseline)" value={label} onChange={(e) => setLabel(e.target.value)} />
                <label style={st.sub}>Target %<input style={st.numIn} type="number" value={target} onChange={(e) => setTarget(e.target.value)} /></label>
                <button style={st.primarySm} disabled={busy} onClick={snapshot}>{busy ? 'Setting…' : 'Set baseline (snapshot now)'}</button>
              </div>
            </div>
          ) : <p style={st.notice}>Load revenue &amp; health first (on the Accounts tab), then come back to set the baseline.</p>
        ) : <p style={st.notice}>No baseline has been set yet — ask Olaf to set the growth baseline.</p>}
      </div>
    );
  }

  const sc = d.scorecard;
  const gp = sc.growthPct;
  const tgt = d.baseline.targetPct;
  const gpColor = gp == null ? '#777' : sc.onTrack ? '#1a7f37' : '#b26a00';

  return (
    <div>
      <div style={st.titleRow}>
        <h2 style={st.h2}>Growth scorecard</h2>
        <span style={st.sub}>{d.baseline.label} · baseline {new Date(d.baseline.snapshotDate).toLocaleDateString('en-US')}</span>
      </div>

      <div style={st.scoreHero}>
        <div style={{ ...st.scoreBig, color: gpColor }}>{gp == null ? '—' : fmtPct(gp)}</div>
        <div style={st.sub}>growth vs baseline · target {fmtPct(tgt)} · <strong style={{ color: gpColor }}>{gp == null ? 'n/a' : sc.onTrack ? 'On track' : 'Behind'}</strong></div>
      </div>

      <div style={st.kpis}>
        <div style={st.kpi}><div style={st.kpiNum}>{fmtMoney(sc.baselineMonthly)}</div><div style={st.kpiLbl}>Baseline / mo</div></div>
        <div style={st.kpi}><div style={st.kpiNum}>{fmtMoney(sc.currentMonthly)}</div><div style={st.kpiLbl}>Current / mo</div></div>
        <div style={st.kpi}><div style={st.kpiNum}>{fmtMoney(sc.netChangeMonthly)}</div><div style={st.kpiLbl}>Net change / mo</div></div>
        <div style={st.kpi}><div style={{ ...st.kpiNum, color: '#1a7f37' }}>{fmtMoney(sc.split.newBusiness)}</div><div style={st.kpiLbl}>New business / mo</div></div>
        <div style={st.kpi}><div style={{ ...st.kpiNum, color: '#1a7f37' }}>{fmtMoney(sc.split.existingGrowth)}</div><div style={st.kpiLbl}>Existing growth / mo</div></div>
      </div>

      {isOwner && (
        <div style={st.chips}>
          <span style={{ ...st.sub, alignSelf: 'center' }}>Target:</span>
          {[['Q1 · 5%', 5], ['Q2 · 10%', 10]].map(([lbl, pct]) => (
            <button key={pct} style={Math.round(tgt * 100) === pct ? { ...st.chip, ...st.chipActive } : st.chip} disabled={busy} onClick={() => changeTarget(pct)}>{lbl}</button>
          ))}
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <input style={st.numIn} type="number" placeholder="%" value={customTarget} onChange={(e) => setCustomTarget(e.target.value)} />
            <button style={st.chip} disabled={busy || customTarget === ''} onClick={() => { changeTarget(Number(customTarget)); setCustomTarget(''); }}>Set custom</button>
          </span>
          <button style={st.chip} disabled={busy} onClick={() => { if (window.confirm('Re-snapshot the baseline to today’s revenue? This resets the starting line.')) snapshot(); }}>Re-baseline</button>
        </div>
      )}

      <h4 style={st.sectionH}>Biggest movers</h4>
      <table style={st.table}>
        <thead><tr><th style={st.th}>Account</th><th style={st.th}>Change</th><th style={st.thR}>Baseline/mo</th><th style={st.thR}>Current/mo</th><th style={st.thR}>Δ/mo</th></tr></thead>
        <tbody>{sc.accounts.slice(0, 40).map((a) => (
          <tr key={a.id}>
            <td style={st.td}>{a.name}</td>
            <td style={st.td}><span style={{ ...st.statusBadge, ...moverStyle(a.category) }}>{MOVER_LABEL[a.category]}</span></td>
            <td style={st.tdR}>{fmtMoney(a.baseline)}</td>
            <td style={st.tdR}>{fmtMoney(a.current)}</td>
            <td style={{ ...st.tdR, color: a.delta > 0 ? '#1a7f37' : a.delta < 0 ? '#b3261e' : '#777' }}>{a.delta > 0 ? '+' : ''}{fmtMoney(a.delta)}</td>
          </tr>
        ))}</tbody>
      </table>
      <p style={st.comingSoon}>Growth = current 3-month-average monthly revenue vs. the frozen baseline. Split into new business (accounts not buying at baseline) and existing growth (more spend from accounts already buying). {isOwner ? '' : 'Scoped to your book.'}</p>
    </div>
  );
}

// Who owns this account. Owners (Olaf) can hand it to someone else; the change
// moves it into that person's book straight away, and SOS's sales rep follows
// via the owner sync.
function OwnerControl({ account, canChange, onChanged }) {
  const [editing, setEditing] = useState(false);
  const [owners, setOwners] = useState(null);
  const [choice, setChoice] = useState(account.ownerId || '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  function start() {
    setEditing(true); setError(''); setChoice(account.ownerId || '');
    if (!owners) salesApi.getOwners().then((r) => setOwners(r.owners)).catch((e) => setError(e.message));
  }
  async function save() {
    setBusy(true); setError('');
    try {
      const res = await salesApi.setAccountOwner(account.key, choice || null);
      onChanged(res);
      setEditing(false);
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  }

  if (!editing) {
    return (
      <span>
        {account.ownerName ? `Owner: ${account.ownerName}` : 'Unassigned'}
        {canChange && <button style={{ ...st.inlineLink, marginLeft: 6 }} onClick={start}>Change</button>}
      </span>
    );
  }
  return (
    <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
      <select style={st.leadInput} value={choice} onChange={(e) => setChoice(e.target.value)} disabled={!owners || busy}>
        <option value="">Unassigned</option>
        {/* the current owner may be someone who has left — keep them visible */}
        {account.ownerId && owners && !owners.some((o) => o.id === account.ownerId) && (
          <option value={account.ownerId}>{account.ownerName || 'Current owner'}</option>
        )}
        {(owners || []).map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
      </select>
      <button style={st.primarySm} disabled={busy || !owners || choice === (account.ownerId || '')} onClick={save}>{busy ? 'Saving…' : 'Save'}</button>
      <button style={st.inlineLink} onClick={() => setEditing(false)} disabled={busy}>Cancel</button>
      {error && <span style={st.err}>{error}</span>}
    </span>
  );
}

// Notes, logged calls/visits and follow-up tasks for one account — HubSpot's
// timeline, owned here. Loads separately from the rest of Customer 360 (which
// waits on a year of SOS orders) so logging a note on a phone is instant.
function AccountActivity({ accountKey, me }) {
  const [d, setD] = useState(null);
  const [error, setError] = useState('');
  const [type, setType] = useState('note');
  const [text, setText] = useState('');
  const [occurredOn, setOccurredOn] = useState(localDay(0));
  const [showTask, setShowTask] = useState(false);
  const [taskTitle, setTaskTitle] = useState('');
  const [taskDue, setTaskDue] = useState(localDay(7));
  const [taskOwner, setTaskOwner] = useState(me?.email || '');
  const [busy, setBusy] = useState(false);
  const [showAll, setShowAll] = useState(false);

  const load = useCallback(() => salesApi.getAccountActivity(accountKey).then(setD).catch((e) => setError(e.message)), [accountKey]);
  useEffect(() => { setD(null); setError(''); load(); }, [load]);

  async function run(fn) {
    setBusy(true); setError('');
    try { await fn(); await load(); } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  }
  const logIt = () => run(async () => {
    await salesApi.logActivity({ key: accountKey, type, text, occurredOn });
    setText(''); setOccurredOn(localDay(0));
  });
  const addTask = () => run(async () => {
    await salesApi.createTask({ key: accountKey, title: taskTitle, dueDate: taskDue, ownerEmail: taskOwner || undefined });
    setTaskTitle(''); setTaskDue(localDay(7)); setShowTask(false);
  });
  const toggle = (t) => run(() => salesApi.updateTask(t.activityId, { done: t.status === 'open' }));

  const staff = d?.staff || [];
  const today = localDay(0);
  const timeline = d?.timeline || [];
  const shown = showAll ? timeline : timeline.slice(0, 10);

  return (
    <div>
      <h4 style={st.sectionH}>Activity</h4>
      {error && <p style={st.err}>{error}</p>}
      {!d && !error && <p style={st.muted}>Loading activity…</p>}
      {d && !d.canLog && <div style={st.notice}>This account isn’t linked to SOS or HubSpot yet, so notes can’t be filed against it.</div>}

      {d && d.canLog && (
        <div style={st.actBox}>
          <div style={st.chips}>
            {ACTIVITY_TYPES.map(([k, label]) => (
              <button key={k} onClick={() => setType(k)} style={{ ...st.chip, ...(type === k ? st.chipActive : {}) }}>{label}</button>
            ))}
          </div>
          <textarea style={st.leadNote} rows={3} value={text} onChange={(e) => setText(e.target.value)}
            placeholder="What happened? e.g. Left voicemail with the GM, dropped off samples…" />
          <div style={st.leadActions}>
            <input style={st.leadInput} type="date" value={occurredOn} max={today} onChange={(e) => setOccurredOn(e.target.value)} aria-label="When" />
            <button style={{ ...st.primarySm, opacity: text.trim() && !busy ? 1 : 0.5 }} disabled={!text.trim() || busy} onClick={logIt}>Save {TYPE_LABEL[type].toLowerCase()}</button>
            <button style={st.outline} onClick={() => setShowTask((v) => !v)}>{showTask ? 'Cancel task' : '+ Follow-up task'}</button>
          </div>
          {showTask && (
            <div style={{ ...st.leadForm, marginTop: 10 }}>
              <label style={{ ...st.leadField, flex: '2 1 220px' }}>What needs doing
                <input style={st.leadInput} value={taskTitle} onChange={(e) => setTaskTitle(e.target.value)} placeholder="e.g. Send cold brew pricing" />
              </label>
              <label style={st.leadField}>Due
                <input style={st.leadInput} type="date" value={taskDue} onChange={(e) => setTaskDue(e.target.value)} />
              </label>
              <label style={st.leadField}>Who
                <select style={st.leadInput} value={taskOwner} onChange={(e) => setTaskOwner(e.target.value)}>
                  {staff.map((p) => <option key={p.email} value={p.email}>{p.email === me?.email ? `Me (${p.name})` : p.name}</option>)}
                </select>
              </label>
              <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                <button style={{ ...st.primarySm, opacity: taskTitle.trim() && !busy ? 1 : 0.5 }} disabled={!taskTitle.trim() || busy} onClick={addTask}>Add task</button>
              </div>
            </div>
          )}
        </div>
      )}

      {d && d.openTasks.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <div style={st.subH}>Open follow-ups</div>
          {d.openTasks.map((t) => (
            <TaskRow key={t.activityId} task={t} today={today} me={me} busy={busy} onToggle={() => toggle(t)} />
          ))}
        </div>
      )}

      {d && (
        <div style={{ marginTop: 12 }}>
          <div style={st.subH}>History</div>
          {timeline.length === 0 && <p style={st.muted}>Nothing logged yet.</p>}
          {shown.map((r) => (
            <div key={r.activityId} style={st.tlRow}>
              <div style={st.tlMeta}>
                <strong style={{ color: '#555' }}>{r.kind === 'task' ? (r.closedAtImport ? 'Task left open in HubSpot' : 'Task done') : TYPE_LABEL[r.type] || 'Note'}</strong>
                {' · '}{r.kind === 'task' ? (r.ownerName || 'Unassigned') : (r.byName || 'HubSpot import')}
                {' · '}{fmtShort(r.sortAt)}
                {r.source === 'hubspot' && <span style={st.fromHs}>from HubSpot</span>}
              </div>
              <div style={st.tlText}>{r.kind === 'task' ? r.title : r.text}</div>
              {r.kind === 'task' && r.text && <div style={st.tlSub}>{r.text}</div>}
            </div>
          ))}
          {timeline.length > shown.length && (
            <button style={st.outline} onClick={() => setShowAll(true)}>Show all {timeline.length}</button>
          )}
        </div>
      )}
    </div>
  );
}

function TaskRow({ task, today, me, busy, onToggle, onOpenAccount }) {
  const overdue = task.status === 'open' && task.dueDate < today;
  const done = task.status === 'done';
  return (
    <div style={st.taskRow}>
      <input type="checkbox" style={st.check} checked={done} disabled={busy} onChange={onToggle} aria-label={done ? 'Mark not done' : 'Mark done'} />
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ ...st.taskTitle, ...(done ? { textDecoration: 'line-through', color: '#999' } : {}) }}>{task.title}</div>
        <div style={st.tlMeta}>
          <span style={overdue ? { color: RED, fontWeight: 600 } : undefined}>{task.dueDate === today ? 'Today' : `Due ${fmtDay(task.dueDate)}`}</span>
          {me && task.owner && task.owner !== String(me.email || '').toLowerCase() && <span> · {task.ownerName}</span>}
          {onOpenAccount && task.accountName && (
            <>
              {' · '}
              {task.accountKey
                ? <button style={st.inlineLink} onClick={() => onOpenAccount(task.accountKey)}>{task.accountName}</button>
                : <span>{task.accountName}</span>}
            </>
          )}
        </div>
        {task.text && <div style={st.tlSub}>{task.text}</div>}
      </div>
    </div>
  );
}

// Everything on my plate, in the order I'd work it: overdue, today, later.
function MyTasks({ onOpenAccount }) {
  const [d, setD] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => salesApi.getMyTasks().then(setD).catch((e) => setError(e.message)), []);
  useEffect(() => { load(); }, [load]);

  async function complete(t) {
    setBusy(true); setError('');
    try { await salesApi.updateTask(t.activityId, { done: true }); await load(); }
    catch (e) { setError(e.message); }
    finally { setBusy(false); }
  }

  const groups = d ? [
    ['Overdue', d.overdue, true],
    ['Today', d.dueToday, false],
    ['Coming up', d.upcoming, false],
  ] : [];
  const total = d ? d.overdue.length + d.dueToday.length + d.upcoming.length : 0;

  return (
    <div>
      <h2 style={st.h2}>My tasks</h2>
      <p style={st.leadIntro}>Follow-ups assigned to you. Tick one off when it’s done; add new ones from an account’s Activity section.</p>
      {error && <p style={st.err}>{error}</p>}
      {!d && !error && <p style={st.muted}>Loading tasks…</p>}
      {d && total === 0 && <p style={st.muted}>Nothing open. ✓</p>}
      {groups.filter(([, list]) => list.length).map(([label, list, red]) => (
        <div key={label} style={{ marginBottom: 16 }}>
          <div style={{ ...st.subH, ...(red ? { color: RED } : {}) }}>{label} ({list.length})</div>
          <div style={st.actBox}>
            {list.map((t) => (
              <TaskRow key={t.activityId} task={t} today={d.today} busy={busy} onToggle={() => complete(t)} onOpenAccount={onOpenAccount} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

const ACTIVITY_TYPES = [['note', 'Note'], ['call', 'Call'], ['visit', 'Visit'], ['email', 'Email']];
const TYPE_LABEL = { note: 'Note', call: 'Call', visit: 'Visit', email: 'Email', meeting: 'Meeting', task: 'Task' };
function localDay(offsetDays) {
  const d = new Date();
  d.setDate(d.getDate() + offsetDays);
  return d.toLocaleDateString('en-CA');
}
const fmtShort = (iso) => new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

// Website enquiries from the marketing site's contact form — one shared queue
// for the whole sales team. Cards rather than a table so it works on a phone
// in the field; tap one to call/email, read the full message, and log what
// happened.
function Leads({ me }) {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('open');
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    salesApi.getLeads().then(setD).catch((e) => setError(e.message)).finally(() => setLoading(false));
  }, []);

  function replaceLead(lead) {
    setD((prev) => {
      const leads = prev.leads.map((l) => (l.leadId === lead.leadId ? lead : l));
      const counts = Object.fromEntries(Object.keys(prev.counts).map((k) => [k, leads.filter((l) => l.status === k).length]));
      return { ...prev, leads, counts };
    });
  }

  const leads = d?.leads || [];
  const counts = d?.counts || {};
  const shown = leads.filter((l) => (filter === 'all' ? true : filter === 'open' ? isOpenLead(l) : l.status === filter));
  const countFor = (key) => (key === 'all' ? leads.length : key === 'open' ? (counts.new || 0) + (counts.following_up || 0) : counts[key] || 0);
  const today = new Date().toLocaleDateString('en-CA');

  return (
    <div>
      <h2 style={st.h2}>Website enquiries</h2>
      <p style={st.leadIntro}>
        From the contact form on silverservice.com. Customer service still gets the email; this is where the team tracks who&rsquo;s on it.
      </p>

      {error && <p style={st.err}>{error}</p>}
      {loading && <p style={st.muted}>Loading enquiries…</p>}

      {d && (
        <div style={st.chips}>
          {LEAD_FILTERS.map(([key, label]) => (
            <button key={key} onClick={() => setFilter(key)} style={{ ...st.chip, ...(filter === key ? st.chipActive : {}) }}>
              {label} ({countFor(key)})
            </button>
          ))}
        </div>
      )}

      {d && shown.length === 0 && <p style={st.muted}>{filter === 'open' ? 'Nothing waiting. ✓' : 'No enquiries here.'}</p>}

      {shown.map((l) => {
        const meta = LEAD_STATUS_META[l.status] || LEAD_STATUS_META.new;
        const expanded = openId === l.leadId;
        const overdue = isOpenLead(l) && l.followUpOn && l.followUpOn <= today;
        return (
          <div key={l.leadId} style={st.leadCard}>
            <div style={st.leadHead} onClick={() => setOpenId(expanded ? null : l.leadId)}>
              <div style={{ minWidth: 0 }}>
                <div style={st.leadTitle}>{l.business || l.name || 'Unknown'}</div>
                <div style={st.leadSub}>{[l.business && l.name ? l.name : null, l.city, l.businessType].filter(Boolean).join(' · ')}</div>
                <div style={st.leadLine}>
                  <span style={l.kind === 'service' ? st.topicService : st.topic}>{l.topic || 'Enquiry'}</span>
                  {l.assignedToName && <span>{l.assignedToName}</span>}
                  {l.followUpOn && isOpenLead(l) && (
                    <span style={overdue ? { color: RED, fontWeight: 600 } : undefined}>Follow up {fmtDay(l.followUpOn)}</span>
                  )}
                </div>
                {!expanded && l.message && <div style={st.leadPreview}>{l.message.length > 160 ? `${l.message.slice(0, 159)}…` : l.message}</div>}
              </div>
              <div style={st.leadRight}>
                <span style={{ ...st.statusBadge, background: meta.bg, color: meta.color }}>{meta.label}</span>
                <div style={st.leadAgo}>{fmtAgo(l.createdAt)}</div>
              </div>
            </div>
            {expanded && <LeadDetail lead={l} staff={d.staff || []} me={me} onSaved={replaceLead} />}
          </div>
        );
      })}
    </div>
  );
}

function LeadDetail({ lead, staff, me, onSaved }) {
  const [status, setStatus] = useState(lead.status);
  const [assignedTo, setAssignedTo] = useState(lead.assignedTo || '');
  const [followUpOn, setFollowUpOn] = useState(lead.followUpOn || '');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const changes = {};
  if (status !== lead.status) changes.status = status;
  if (assignedTo !== (lead.assignedTo || '')) changes.assignedTo = assignedTo || null;
  if (followUpOn !== (lead.followUpOn || '')) changes.followUpOn = followUpOn || null;
  if (note.trim()) changes.note = note.trim();
  const dirty = Object.keys(changes).length > 0;
  const phoneDigits = String(lead.phone || '').replace(/[^0-9+]/g, '');
  const canTakeIt = me?.email && lead.assignedTo !== me.email && staff.some((s) => s.email === me.email);

  async function save(extra = {}) {
    setBusy(true);
    setError('');
    try {
      const res = await salesApi.updateLead(lead.leadId, { ...changes, ...extra });
      onSaved(res.lead);
      setStatus(res.lead.status);
      setAssignedTo(res.lead.assignedTo || '');
      setFollowUpOn(res.lead.followUpOn || '');
      setNote('');
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  }

  return (
    <div style={st.leadBody}>
      <div style={st.leadContact}>
        {lead.phone && <a style={st.contactBtn} href={`tel:${phoneDigits}`}>Call {lead.phone}</a>}
        {lead.email && <a style={st.contactBtn} href={`mailto:${lead.email}`}>Email {lead.email}</a>}
      </div>
      {lead.message && <div style={st.leadMessage}>{lead.message}</div>}
      <div style={st.leadReceived}>Received {fmtWhen(lead.createdAt)} via the website contact form</div>

      <div style={st.leadForm}>
        <label style={st.leadField}>Status
          <select style={st.leadInput} value={status} onChange={(e) => setStatus(e.target.value)}>
            {Object.entries(LEAD_STATUS_META).map(([k, m]) => <option key={k} value={k}>{m.label}</option>)}
          </select>
        </label>
        <label style={st.leadField}>Assigned to
          <select style={st.leadInput} value={assignedTo} onChange={(e) => setAssignedTo(e.target.value)}>
            <option value="">Nobody yet</option>
            {staff.map((s) => <option key={s.email} value={s.email}>{s.name}</option>)}
          </select>
        </label>
        <label style={st.leadField}>Follow up on
          <input style={st.leadInput} type="date" value={followUpOn} onChange={(e) => setFollowUpOn(e.target.value)} />
        </label>
      </div>
      <textarea
        style={st.leadNote}
        rows={3}
        placeholder="What happened? e.g. Left voicemail, booked a tasting for Thursday…"
        value={note}
        onChange={(e) => setNote(e.target.value)}
      />
      <div style={st.leadActions}>
        <button style={{ ...st.primarySm, opacity: dirty && !busy ? 1 : 0.5 }} disabled={!dirty || busy} onClick={() => save()}>
          {busy ? 'Saving…' : 'Save'}
        </button>
        {canTakeIt && (
          <button style={st.outline} disabled={busy} onClick={() => save({ assignedTo: me.email })}>Assign to me</button>
        )}
        {error && <span style={st.err}>{error}</span>}
      </div>

      {(lead.activity || []).length > 0 && (
        <div style={st.leadActivity}>
          {[...lead.activity].reverse().map((a, i) => (
            <div key={i} style={st.activityRow}>
              <div style={a.type === 'note' ? st.activityNote : st.activityEvent}>{a.text}</div>
              <div style={st.activityMeta}>{a.byName || a.by || 'Someone'} · {fmtWhen(a.at)}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const isOpenLead = (l) => l.status === 'new' || l.status === 'following_up';
const fmtDay = (ymd) => new Date(`${ymd}T12:00:00`).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
const fmtWhen = (iso) => new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
function fmtAgo(iso) {
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  if (Number.isNaN(days)) return '';
  return days <= 0 ? 'today' : days === 1 ? 'yesterday' : `${days} days ago`;
}

// Small hover/tap "i" with an explanatory tooltip. align = 'left' | 'right'
// controls which edge the bubble hangs from (so it doesn't run off-screen).
function InfoTip({ children, align = 'left', width = 280 }) {
  const [show, setShow] = useState(false);
  return (
    <span
      style={st.infoWrap}
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}
      onClick={(e) => { e.stopPropagation(); setShow((s) => !s); }}
    >
      <span style={st.infoIcon}>i</span>
      {show && <span style={{ ...st.tip, width, [align]: 0 }}>{children}</span>}
    </span>
  );
}

const HEALTH_HELP = (
  <span>
    <strong style={{ color: '#7bdc9a' }}>Healthy</strong> — ordering on their usual rhythm.<br />
    <strong style={{ color: '#ffce85' }}>At-risk</strong> — silent for more than 1.5× their usual gap between orders (late for them), but under 90 days.<br />
    <strong style={{ color: '#ff9b94' }}>Dormant</strong> — no order in 90+ days.<br /><br />
    “Cadence” is the median gap between this account’s orders, so the same silence flags a frequent buyer sooner than an occasional one.
  </span>
);

const METRICS_HELP = (
  <span>
    <strong>How these are calculated</strong>, from SOS orders (refreshed every few hours):<br /><br />
    <strong>Spend:</strong> orders by order date, quantity × unit price, equipment left out — the same basis as the bonus sheet.<br />
    <strong>Avg / month:</strong> the last 12 full months ÷ 12 (or ÷ months since their first order, if newer).<br />
    <strong>Quarters:</strong> calendar quarters; “so far” runs to today. ▲▼ compares the same days a year earlier. SOS history starts 20 Aug 2025, so earlier comparisons are blank.<br />
    <strong>GP:</strong> spend minus cost. Cost is SOS’s own line cost, or the item’s current cost where SOS has none.<br />
    <strong>Last order:</strong> date of their most recent order (⚠ = overdue).<br /><br />
    {HEALTH_HELP}
  </span>
);

const SPEND_SORTS = {
  avgMonthly: (a) => (a.spend && a.spend.avgMonthly != null ? a.spend.avgMonthly : -1),
  thisQuarter: (a) => (a.spend ? a.spend.thisQuarter.spend : -1),
  lastQuarter: (a) => (a.spend ? a.spend.lastQuarter.spend : -1),
  gp: (a) => (a.spend ? a.spend.last12.gp : -Infinity),
};

// Book totals for whatever is filtered. GP % is weighted by the spend the GP
// rests on, not an average of percentages.
function bookTotals(list) {
  const t = { avgMonthly: 0, thisQuarter: 0, thisQuarterLy: null, lastQuarter: 0, lastQuarterLy: null, gp: 0, costed: 0 };
  for (const a of list) {
    const sp = a.spend;
    if (!sp) continue;
    t.avgMonthly += sp.avgMonthly || 0;
    t.thisQuarter += sp.thisQuarter.spend;
    t.lastQuarter += sp.lastQuarter.spend;
    if (sp.thisQuarter.lastYear != null) t.thisQuarterLy = (t.thisQuarterLy || 0) + sp.thisQuarter.lastYear;
    if (sp.lastQuarter.lastYear != null) t.lastQuarterLy = (t.lastQuarterLy || 0) + sp.lastQuarter.lastYear;
    t.gp += sp.last12.gp;
    t.costed += sp.last12.spend * (sp.last12.costCoverage || 0);
  }
  t.gpPct = t.costed > 0 ? (t.gp / t.costed) * 100 : null;
  return t;
}

// ▲/▼ against the same period a year earlier; nothing when there's no history.
function YoY({ now, then }) {
  if (then == null) return null;
  if (!then) return now > 0 ? <div style={st.yoyFlat}>new vs last year</div> : null;
  const pct = ((now - then) / then) * 100;
  const up = pct >= 0;
  return <div style={up ? st.yoyUp : st.yoyDown} title={`Same period last year: ${fmtMoney(then)}`}>{up ? '▲' : '▼'} {Math.abs(pct).toFixed(0)}% vs last year</div>;
}

function SortTh({ k, sortKey, setSortKey, tip, children }) {
  const active = sortKey === k;
  return (
    <th style={{ ...st.thR, cursor: 'pointer', color: active ? RED : st.thR.color }} onClick={() => setSortKey(active ? '' : k)} title="Sort by this column (biggest first)">
      {children}{active ? ' ▼' : ''}<InfoTip align="right" width={260}>{tip}</InfoTip>
    </th>
  );
}

function SpendCells({ spend }) {
  const dash = <span style={st.muted}>{'\u2014'}</span>;
  if (!spend) return <><td style={st.tdR}>{dash}</td><td style={st.tdR}>{dash}</td><td style={st.tdR}>{dash}</td><td style={st.tdR}>{dash}</td></>;
  const lowCost = spend.last12.costCoverage != null && spend.last12.costCoverage < 0.9;
  return (
    <>
      <td style={st.tdR}>{spend.avgMonthly != null ? fmtMoney(spend.avgMonthly) : dash}</td>
      <td style={st.tdR}>{fmtMoney(spend.thisQuarter.spend)}<YoY now={spend.thisQuarter.spend} then={spend.thisQuarter.lastYear} /></td>
      <td style={st.tdR}>{fmtMoney(spend.lastQuarter.spend)}<YoY now={spend.lastQuarter.spend} then={spend.lastQuarter.lastYear} /></td>
      <td style={st.tdR} title={lowCost ? `Cost known for ${Math.round(spend.last12.costCoverage * 100)}% of this spend` : undefined}>
        {fmtMoney(spend.last12.gp)}{lowCost ? '*' : ''}
        {spend.last12.gpPct != null && <div style={st.subNum}>{spend.last12.gpPct}%</div>}
      </td>
    </>
  );
}

// The AI summary and the staff notes (delivery instructions, machine issues)
// for the account — one SOS customer at a time for multi-site accounts. The
// same component Manage uses for notes, called through the sales login.
function AccountSynopsis({ account, accountKey, me }) {
  const sites = account.sosCustomers || [];
  const [customerId, setCustomerId] = useState(sites[0] ? sites[0].id : null);
  const api = useMemo(() => ({
    get: (id) => salesApi.getSynopsis(accountKey, id),
    addNote: (id, note) => salesApi.addSynopsisNote(accountKey, id, note),
    deleteNote: (id, noteId) => salesApi.deleteSynopsisNote(accountKey, id, noteId),
  }), [accountKey]);
  if (!customerId) return null;
  const site = sites.find((c) => c.id === customerId) || sites[0];
  return (
    <>
      <h4 style={st.sectionH}>
        Summary and staff notes
        <InfoTip align="left" width={280}>An AI-written summary of what this customer buys, and notes from the team — delivery instructions, machine issues, preferences.</InfoTip>
      </h4>
      {sites.length > 1 && (
        <select style={{ ...st.select, marginBottom: 10 }} value={customerId} onChange={(e) => setCustomerId(e.target.value)} aria-label="Location">
          {sites.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      )}
      <CustomerSynopsis key={customerId} customer={{ id: customerId, name: site.name }} me={{ email: me?.email, role: me?.isOwner ? 'owner' : me?.role }} api={api} sections={['summary', 'notes']} />
    </>
  );
}

// Win-back: the caller's accounts that have gone quiet, biggest first, with
// the numbers to call. Moved here from Manage (docs/site-map.md).
function Winback({ onOpenAccount }) {
  const [d, setD] = useState(null);
  const [error, setError] = useState('');
  const [q, setQ] = useState('');
  const [copied, setCopied] = useState('');
  useEffect(() => { salesApi.getWinback().then(setD).catch((e) => setError(e.message)); }, []);
  const rows = useMemo(() => (d?.accounts || []).filter((a) => !q || a.name.toLowerCase().includes(q.toLowerCase())), [d, q]);

  function copyList() {
    const header = ['Account', 'Owner', 'Status', 'Days quiet', 'Last order', 'Avg per month', 'Phone', 'Email'].join('\t');
    const body = rows.map((a) => [a.name, a.ownerName || '', STATUS_META[a.status].label, a.daysSinceLast ?? '', a.lastOrderDate || '', Math.round(a.avgMonthly || 0), a.phones.join(', '), a.emails.join(', ')].join('\t'));
    navigator.clipboard.writeText([header, ...body].join('\n'))
      .then(() => setCopied(`Copied ${rows.length}`))
      .catch(() => setCopied('Copy failed'))
      .finally(() => setTimeout(() => setCopied(''), 2500));
  }

  if (error) return <p style={st.err}>{error}</p>;
  if (!d) return <p style={st.muted}>Loading…</p>;
  return (
    <div>
      <div style={st.titleRow}>
        <h2 style={st.h2}>Win-back</h2>
        <button style={st.outline} onClick={copyList} disabled={!rows.length}>{copied || 'Copy list'}</button>
      </div>
      <p style={st.muted}>
        {d.all ? 'Every account' : 'Each of your accounts'} that has gone quiet — late for its usual rhythm (at-risk) or nothing in 90+ days (dormant). Biggest first.
        Open one to log the call.
      </p>
      <div style={st.kpis}>
        <div style={st.kpi}><div style={st.kpiNum}>{d.accounts.length}</div><div style={st.kpiLbl}>Accounts gone quiet</div></div>
        <div style={st.kpi}><div style={st.kpiNum}>{fmtMoney(d.monthlyAtStake)}</div><div style={st.kpiLbl}>Their usual spend per month</div></div>
      </div>
      <input style={st.search} placeholder="Search accounts…" value={q} onChange={(e) => setQ(e.target.value)} />
      {rows.length === 0 ? <p style={st.muted}>Nobody has gone quiet. 👍</p> : (
        <div style={st.tableScroll}>
          <table style={st.table}>
            <thead><tr>
              <th style={st.th}>Account</th>
              {d.all && <th style={st.th}>Owner</th>}
              <th style={st.th}>Status</th>
              <th style={st.th}>Last order</th>
              <th style={st.thR}>Avg&nbsp;/&nbsp;mo</th>
              <th style={st.th}>Contact</th>
            </tr></thead>
            <tbody>{rows.map((a) => (
              <tr key={a.key} style={st.rowClickable} onClick={() => onOpenAccount(a.key)}>
                <td style={st.tdLink}>{a.name}</td>
                {d.all && <td style={st.td}>{a.ownerName || '\u2014'}</td>}
                <td style={st.td}><span style={{ ...st.statusBadge, background: STATUS_META[a.status].bg, color: STATUS_META[a.status].color }}>{STATUS_META[a.status].label}</span></td>
                <td style={st.td}>
                  {a.lastOrderDate || '\u2014'}
                  <div style={st.subNum}>{a.daysSinceLast != null ? `${a.daysSinceLast} days ago` : ''}{a.cadenceDays ? ` · usually every ${a.cadenceDays}` : ''}</div>
                </td>
                <td style={st.tdR}>{a.avgMonthly != null ? fmtMoney(a.avgMonthly) : '\u2014'}</td>
                <td style={st.td} onClick={(e) => e.stopPropagation()}>
                  {a.phones.map((ph) => <div key={ph}><a href={`tel:${ph.replace(/[^\d+]/g, '')}`} style={st.contactLink}>{ph}</a></div>)}
                  {a.emails.map((em) => <div key={em}><a href={`mailto:${em}`} style={st.contactLink}>{em}</a></div>)}
                  {!a.phones.length && !a.emails.length && <span style={st.muted}>No contact on file</span>}
                </td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// Customer 360: spend and GP by calendar quarter, oldest first.
function QuarterTable({ quarters }) {
  return (
    <>
      <h4 style={st.sectionH}>Spend and GP by quarter<InfoTip align="left" width={280}>Same basis as the bonus sheet: quantity × unit price, equipment left out. GP is spend minus cost.</InfoTip></h4>
      <div style={st.tableScroll}>
        <table style={st.table}>
          <thead><tr><th style={st.th}>Quarter</th><th style={st.thR}>Spend</th><th style={st.thR}>GP</th><th style={st.thR}>GP %</th></tr></thead>
          <tbody>{quarters.map((q) => (
            <tr key={q.key}>
              <td style={st.td}>{q.label}{q.partial ? ' (so far)' : ''}{q.beforeHistory ? <span style={st.muted}> · SOS history starts 20 Aug 2025</span> : null}</td>
              <td style={st.tdR}>{fmtMoney(q.spend)}</td>
              <td style={st.tdR}>{fmtMoney(q.gp)}</td>
              <td style={st.tdR}>{q.gpPct != null ? `${q.gpPct}%` : '\u2014'}</td>
            </tr>
          ))}</tbody>
        </table>
      </div>
    </>
  );
}

// Silver Service brand: deep red on light, matching the customer ordering site.
const RED = '#A50B18';
const LEAD_STATUS_META = {
  new: { label: 'New', color: RED, bg: '#fdecea' },
  following_up: { label: 'Following up', color: '#b26a00', bg: '#fff4e5' },
  converted: { label: 'Became a customer', color: '#1a7f37', bg: '#e6f4ea' },
  closed: { label: 'Closed', color: '#666', bg: '#f0f0f0' },
};
const LEAD_FILTERS = [
  ['open', 'Open'], ['new', 'New'], ['following_up', 'Following up'],
  ['converted', 'Became a customer'], ['closed', 'Closed'], ['all', 'All'],
];
const st = {

  page: { minHeight: '100vh', background: '#f5f5f5', color: '#333' },
  topBar: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 24px', background: RED, color: '#fff' },
  logoImg: { height: 30, display: 'block' },
  brandSmSub: { marginLeft: 10, color: 'rgba(255,255,255,0.75)', textTransform: 'uppercase', letterSpacing: 2, fontSize: '.65rem' },
  topRight: { display: 'flex', alignItems: 'center', gap: 16 },
  who: { color: 'rgba(255,255,255,0.9)', fontSize: '.85rem' },
  linkBtn: { background: 'none', border: '1px solid rgba(255,255,255,0.5)', color: '#fff', cursor: 'pointer', fontSize: '.8rem', padding: '4px 10px', borderRadius: 6 },

  body: { maxWidth: 1100, margin: '0 auto', padding: '24px' },
  titleRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  h2: { margin: 0, fontSize: '1.4rem', color: '#333' },
  primarySm: { padding: '8px 16px', background: RED, color: '#fff', border: 'none', borderRadius: 6, fontWeight: 600, cursor: 'pointer', fontSize: '.9rem' },
  chips: { display: 'flex', gap: 8, margin: '4px 0 12px', flexWrap: 'wrap' },
  chip: { padding: '5px 12px', background: '#fff', border: '1px solid #ddd', borderRadius: 999, cursor: 'pointer', fontSize: '.82rem', color: '#555' },
  chipActive: { border: `1px solid ${RED}`, background: '#fff0f0', fontWeight: 600 },
  statusBadge: { display: 'inline-block', padding: '2px 10px', borderRadius: 999, fontSize: '.74rem', fontWeight: 600 },
  thR: { textAlign: 'right', padding: '10px 14px', borderBottom: '1px solid #ececec', color: '#777', fontSize: '.78rem', textTransform: 'uppercase', letterSpacing: 1 },
  tdR: { textAlign: 'right', padding: '10px 14px', borderBottom: '1px solid #f3f3f3', fontSize: '.92rem', color: '#333', fontVariantNumeric: 'tabular-nums' },
  notice: { margin: '12px 0', padding: '12px 16px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, fontSize: '.9rem', color: '#333' },
  kpis: { display: 'flex', flexWrap: 'wrap', gap: 12, margin: '16px 0' },
  kpi: { flex: '1 1 150px', background: '#fff', border: '1px solid #e0e0e0', borderRadius: 10, padding: '14px 16px' },
  tableScroll: { width: '100%', overflowX: 'auto' },
  filterRow: { display: 'flex', flexWrap: 'wrap', gap: 8, margin: '4px 0 12px' },
  select: { padding: '10px 12px', border: '1px solid #ddd', borderRadius: 6, fontSize: '.95rem', background: '#fff', color: '#333' },
  subNum: { color: '#888', fontSize: '.78rem' },
  yoyUp: { color: '#1a7f37', fontSize: '.74rem', whiteSpace: 'nowrap' },
  yoyDown: { color: '#b3261e', fontSize: '.74rem', whiteSpace: 'nowrap' },
  yoyFlat: { color: '#888', fontSize: '.74rem', whiteSpace: 'nowrap' },
  contactLink: { color: '#1a5fb4', textDecoration: 'none', fontSize: '.86rem' },
  kpiNum: { fontSize: '1.6rem', fontWeight: 700, color: RED },
  kpiLbl: { color: '#777', fontSize: '.8rem', marginTop: 2 },
  search: { width: '100%', padding: '10px 12px', border: '1px solid #ddd', borderRadius: 6, fontSize: '.95rem', margin: '4px 0 12px', boxSizing: 'border-box' },
  table: { width: '100%', borderCollapse: 'collapse', background: '#fff', border: '1px solid #e0e0e0', borderRadius: 10 },
  asOf: { display: 'flex', alignItems: 'center', color: '#888', fontSize: '.82rem', margin: '0 0 10px' },
  infoWrap: { position: 'relative', display: 'inline-flex', alignItems: 'center', marginLeft: 5, cursor: 'help', verticalAlign: 'middle' },
  infoIcon: { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 15, height: 15, borderRadius: '50%', background: '#bbb', color: '#fff', fontSize: 10, fontWeight: 700, fontStyle: 'italic', fontFamily: 'Georgia, serif', lineHeight: 1 },
  tip: { position: 'absolute', top: '150%', zIndex: 50, background: '#222', color: '#fff', padding: '10px 12px', borderRadius: 8, fontSize: '.78rem', lineHeight: 1.55, fontWeight: 400, textTransform: 'none', letterSpacing: 0, boxShadow: '0 6px 20px rgba(0,0,0,0.3)', textAlign: 'left', whiteSpace: 'normal' },
  th: { textAlign: 'left', padding: '10px 14px', borderBottom: '1px solid #ececec', color: '#777', fontSize: '.78rem', textTransform: 'uppercase', letterSpacing: 1 },
  td: { padding: '10px 14px', borderBottom: '1px solid #f3f3f3', fontSize: '.92rem', color: '#333' },
  badge: { display: 'inline-block', padding: '2px 8px', marginRight: 4, background: '#f3f3f3', borderRadius: 999, fontSize: '.72rem', color: '#555' },
  outline: { padding: '8px 14px', background: '#fff', border: '1px solid #ddd', borderRadius: 6, cursor: 'pointer', color: '#333' },
  muted: { color: '#999', fontSize: '.85rem' },
  err: { color: '#c0392b', fontSize: '.9rem' },
  rowClickable: { cursor: 'pointer' },
  // Calm link-blue, not brand-red — red on a name reads like an alert.
  tdLink: { padding: '10px 14px', borderBottom: '1px solid #f3f3f3', fontSize: '.92rem', color: '#1a5fb4', fontWeight: 500 },

  backBtn: { background: 'none', border: 'none', color: RED, cursor: 'pointer', fontSize: '.9rem', padding: '4px 0', marginBottom: 6 },
  detailHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, margin: '4px 0 14px' },
  detailSub: { color: '#777', fontSize: '.88rem', marginTop: 6, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' },
  sectionH: { fontSize: '1rem', color: '#333', margin: '22px 0 8px' },
  bars: { display: 'flex', alignItems: 'flex-end', gap: 6, height: 150, padding: '10px 6px', background: '#fff', border: '1px solid #e0e0e0', borderRadius: 10 },
  barCol: { flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end', height: '100%' },
  bar: { width: '70%', maxWidth: 38, background: RED, borderRadius: '4px 4px 0 0', minHeight: 2 },
  barLabel: { fontSize: '.68rem', color: '#999', marginTop: 5, whiteSpace: 'nowrap' },
  twoCol: { display: 'flex', gap: 18, marginTop: 4, flexWrap: 'wrap' },
  rangeBar: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' },
  rangeDates: { display: 'flex', alignItems: 'center', gap: 8 },
  dateInput: { padding: '5px 8px', border: '1px solid #ddd', borderRadius: 6, fontSize: '.85rem', background: '#fff', color: '#333' },
  rangeSummary: { fontSize: '.92rem', color: '#333', margin: '10px 0 4px' },
  subH: { fontSize: '.9rem', color: '#555', margin: '14px 0 8px' },
  scrollBox: { maxHeight: 440, overflowY: 'auto', borderRadius: 10 },
  col: { flex: 1, minWidth: 280 },
  arLine: { fontSize: '.95rem', margin: '4px 0 8px', color: '#333' },
  comingSoon: { color: '#999', fontSize: '.82rem', fontStyle: 'italic', marginTop: 24 },
  lastAct: { color: '#999', fontSize: '.82rem', fontWeight: 400 },
  viewTabs: { display: 'flex', gap: 4, marginBottom: 16, borderBottom: '1px solid #e0e0e0' },
  viewTab: { background: 'none', border: 'none', borderBottom: '2px solid transparent', padding: '8px 14px', cursor: 'pointer', color: '#777', fontSize: '.95rem' },
  viewTabActive: { color: RED, borderBottom: `2px solid ${RED}`, fontWeight: 600 },
  scoreHero: { textAlign: 'center', padding: '18px', background: '#fff', border: '1px solid #e0e0e0', borderRadius: 12, margin: '4px 0 16px' },
  scoreBig: { fontSize: '3rem', fontWeight: 800, lineHeight: 1 },
  baselineForm: { display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginTop: 10 },
  numIn: { width: 64, padding: '8px 10px', border: '1px solid #ddd', borderRadius: 6, marginLeft: 8 },

  leadIntro: { color: '#777', fontSize: '.88rem', margin: '6px 0 14px' },
  leadCard: { background: '#fff', border: '1px solid #e0e0e0', borderRadius: 10, marginBottom: 10 },
  leadHead: { display: 'flex', justifyContent: 'space-between', gap: 12, padding: '12px 14px', cursor: 'pointer' },
  leadTitle: { fontWeight: 600, fontSize: '1rem', color: '#222' },
  leadSub: { color: '#777', fontSize: '.85rem', marginTop: 2 },
  leadLine: { display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', fontSize: '.82rem', color: '#555', marginTop: 6 },
  topic: { padding: '2px 8px', background: '#f3f3f3', borderRadius: 999, color: '#444' },
  topicService: { padding: '2px 8px', background: '#fdecea', borderRadius: 999, color: RED, fontWeight: 600 },
  leadPreview: { color: '#666', fontSize: '.85rem', marginTop: 6, lineHeight: 1.45, overflowWrap: 'anywhere' },
  leadRight: { textAlign: 'right', flexShrink: 0 },
  leadAgo: { color: '#999', fontSize: '.78rem', marginTop: 6 },
  leadBody: { borderTop: '1px solid #f0f0f0', padding: '12px 14px 14px' },
  leadContact: { display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 },
  contactBtn: { padding: '8px 12px', border: '1px solid #ddd', borderRadius: 6, color: '#1a5fb4', textDecoration: 'none', fontSize: '.9rem', overflowWrap: 'anywhere' },
  leadMessage: { whiteSpace: 'pre-wrap', fontSize: '.92rem', lineHeight: 1.5, color: '#333', background: '#fafafa', borderRadius: 6, padding: '10px 12px', overflowWrap: 'anywhere' },
  leadReceived: { color: '#999', fontSize: '.78rem', margin: '6px 0 12px' },
  leadForm: { display: 'flex', gap: 10, flexWrap: 'wrap' },
  leadField: { display: 'flex', flexDirection: 'column', gap: 4, fontSize: '.78rem', color: '#777', flex: '1 1 160px' },
  leadInput: { padding: '8px 10px', border: '1px solid #ddd', borderRadius: 6, fontSize: '.92rem', background: '#fff', color: '#333' },
  leadNote: { width: '100%', boxSizing: 'border-box', marginTop: 10, padding: '8px 10px', border: '1px solid #ddd', borderRadius: 6, fontSize: '.92rem', fontFamily: 'inherit', resize: 'vertical' },
  leadActions: { display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginTop: 8 },
  leadActivity: { marginTop: 14, borderTop: '1px solid #f0f0f0', paddingTop: 10 },
  activityRow: { padding: '6px 0' },
  activityNote: { fontSize: '.9rem', color: '#333', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' },
  activityEvent: { fontSize: '.85rem', color: '#777', fontStyle: 'italic' },
  activityMeta: { fontSize: '.75rem', color: '#aaa', marginTop: 2 },

  actBox: { background: '#fff', border: '1px solid #e0e0e0', borderRadius: 10, padding: '12px 14px' },
  subH: { fontSize: '.78rem', color: '#777', textTransform: 'uppercase', letterSpacing: 1, margin: '0 0 6px' },
  taskRow: { display: 'flex', gap: 10, alignItems: 'flex-start', padding: '8px 0', borderBottom: '1px solid #f3f3f3' },
  check: { width: 20, height: 20, marginTop: 2, flexShrink: 0, accentColor: RED, cursor: 'pointer' },
  taskTitle: { fontSize: '.95rem', color: '#222', overflowWrap: 'anywhere' },
  inlineLink: { background: 'none', border: 'none', padding: 0, color: '#1a5fb4', cursor: 'pointer', font: 'inherit' },
  tlRow: { background: '#fff', border: '1px solid #ececec', borderRadius: 8, padding: '10px 12px', marginBottom: 8 },
  tlMeta: { fontSize: '.78rem', color: '#888', display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' },
  tlText: { fontSize: '.92rem', color: '#333', marginTop: 4, whiteSpace: 'pre-wrap', lineHeight: 1.45, overflowWrap: 'anywhere' },
  tlSub: { fontSize: '.85rem', color: '#666', marginTop: 4, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' },
  fromHs: { marginLeft: 6, padding: '1px 6px', background: '#fff4ec', color: '#c2410c', borderRadius: 999, fontSize: '.68rem' },
};
